import { AlertTriangle, Phone01, Signal02 } from "@untitledui/icons";
import { type ReactNode, useEffect, useState } from "react";
// Exceção intencional: Toggle ainda não está exportado pelo barrel @components.
import { Toggle } from "@/components/base/toggle/toggle";
import { useToast } from "@/providers/toast-provider";
import { useUpdateVehicleTelemetry } from "../query";
import {
	TELEMETRY_SOURCE,
	type TelemetryCardProps,
	type TelemetrySource,
} from "../types";

const COMMERCIAL_WHATSAPP_URL =
	"https://wa.me/556296655802?text=Ol%C3%A1%2C%20quero%20contratar%20Telemetria.";
const COMMERCIAL_WHATSAPP_DISPLAY = "+55 62 9665-5802";

type TelemetryOption = {
	source: TelemetrySource;
	title: string;
	description: string;
	icon: ReactNode;
};

const normalizeTelemetrySource = (
	value?: TelemetrySource | null
): TelemetrySource => {
	if (value === null || value === undefined) {
		return TELEMETRY_SOURCE.notDefined;
	}

	const numericValue = Number(value);
	if (
		numericValue === TELEMETRY_SOURCE.cellPhone ||
		numericValue === TELEMETRY_SOURCE.hardware
	) {
		return numericValue as TelemetrySource;
	}

	return TELEMETRY_SOURCE.notDefined;
};

const OPTIONS: TelemetryOption[] = [
	{
		source: TELEMETRY_SOURCE.cellPhone,
		title: "Celular",
		description: "Os dados de telemetria serão obtidos à partir do celular.",
		icon: <Phone01 className="size-6 text-fg-quaternary" />,
	},
	{
		source: TELEMETRY_SOURCE.hardware,
		title: "Hardware",
		description:
			"Dados mais detalhados de telemetria serão obtidos à partir do hardware instalado no veículo.",
		icon: <Signal02 className="size-6 text-fg-quaternary" />,
	},
];

const TELEMETRY_HARDWARE_MESSAGES = {
	cannotDeactivate:
		"A Telemetria por hardware não pode ser ativada. Para contratar, fale com o time do comercial.",
	cannotActivate:
		"A Telemetria por hardware não pode ser ativada. Para contratar, fale com o time do comercial.",
} as const;

type TelemetryOptionRowProps = {
	option: TelemetryOption;
	effectiveSource: TelemetrySource;
	isPending: boolean;
	onChange: (source: TelemetrySource, isOn: boolean) => void;
};

const TelemetryOptionRow = ({
	option,
	effectiveSource,
	isPending,
	onChange,
}: TelemetryOptionRowProps) => {
	const isSelected = effectiveSource === option.source;
	const hardwareIsActive = effectiveSource === TELEMETRY_SOURCE.hardware;
	const isHardwareOption = option.source === TELEMETRY_SOURCE.hardware;
	const isCellPhoneOption = option.source === TELEMETRY_SOURCE.cellPhone;
	const isHardwareLocked = isHardwareOption ? isSelected : false;

	return (
		<div className="flex items-center gap-4 rounded border border-secondary bg-secondary_alt p-4 shadow-xs">
			<div className="flex size-6 shrink-0 items-center justify-center">
				{option.icon}
			</div>

			<div className="flex flex-1 flex-col">
				<p className="font-bold text-secondary text-sm leading-5">
					{option.title}
				</p>
				<p className="font-medium text-tertiary text-xs leading-[18px]">
					{option.description}
				</p>
			</div>

			<Toggle
				aria-label={`Ativar telemetria por ${option.title}`}
				isDisabled={
					isPending ||
					(isHardwareOption ? !isSelected : false) ||
					(isCellPhoneOption ? hardwareIsActive : false)
				}
				isSelected={isSelected}
				onChange={(value) => onChange(option.source, value)}
				showNotAllowedCursor={isHardwareLocked}
				size="md"
				title={
					isHardwareLocked ? TELEMETRY_HARDWARE_MESSAGES.cannotDeactivate : ""
				}
			/>
		</div>
	);
};

export const TelemetryCard = ({
	vehicleId,
	telemetrySource,
}: TelemetryCardProps) => {
	const [activeSource, setActiveSource] = useState<TelemetrySource>(() =>
		normalizeTelemetrySource(telemetrySource)
	);
	const updateTelemetry = useUpdateVehicleTelemetry();
	const { showToast } = useToast();

	useEffect(() => {
		setActiveSource(normalizeTelemetrySource(telemetrySource));
	}, [telemetrySource]);

	const handleChange = (source: TelemetrySource, isOn: boolean) => {
		if (updateTelemetry.isPending) {
			return;
		}

		// Regra: hardware não pode ser ativado pelo usuário
		if (source === TELEMETRY_SOURCE.hardware && isOn) {
			showToast({
				variant: "error",
				title: TELEMETRY_HARDWARE_MESSAGES.cannotActivate,
			});
			return;
		}

		// Regra: hardware não pode ser desativado pelo usuário
		if (source === TELEMETRY_SOURCE.hardware && !isOn) {
			showToast({
				variant: "error",
				title: TELEMETRY_HARDWARE_MESSAGES.cannotDeactivate,
			});
			return;
		}

		// Regra: se hardware estiver ativo, celular não pode ser ativado
		if (
			source === TELEMETRY_SOURCE.cellPhone &&
			isOn &&
			activeSource === TELEMETRY_SOURCE.hardware
		) {
			showToast({
				variant: "error",
				title:
					"Não é possível ativar o celular enquanto o hardware estiver ativo",
			});
			return;
		}

		const nextSource: TelemetrySource = isOn
			? source
			: TELEMETRY_SOURCE.notDefined;
		if (nextSource === activeSource) {
			return;
		}

		const previousSource = activeSource;
		setActiveSource(nextSource);
		updateTelemetry.mutate(
			{ vehicleId, payload: { telemetrySource: nextSource } },
			{
				onError: () => {
					setActiveSource(previousSource);
					showToast({
						variant: "error",
						title: "Erro ao atualizar telemetria",
					});
				},
				onSuccess: () => {
					showToast({
						variant: "success",
						title: "Telemetria atualizada com sucesso",
					});
				},
			}
		);
	};

	return (
		<div className="flex flex-col gap-6 rounded-xl border border-primary bg-bg-primary p-6 shadow-xs">
			<div className="flex flex-col gap-1">
				<h3 className="font-bold text-md text-secondary leading-6">
					Tipo de dispositivo
				</h3>
				<p className="text-quaternary text-xs leading-[18px]">
					Selecione o dispositivo de telemetria.
				</p>
			</div>

			<fieldset className="flex flex-col gap-3">
				<legend className="sr-only">Tipo de dispositivo de telemetria</legend>
				{OPTIONS.map((option) => (
					<TelemetryOptionRow
						effectiveSource={normalizeTelemetrySource(
							telemetrySource ?? activeSource
						)}
						isPending={updateTelemetry.isPending}
						key={option.source}
						onChange={handleChange}
						option={option}
					/>
				))}
			</fieldset>

			<div className="flex items-start gap-4 rounded-lg border border-utility-warning-500 bg-warning-secondary p-5">
				<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-bg-primary shadow-xs">
					<AlertTriangle className="size-6 text-fg-warning-primary" />
				</div>
				<p className="font-semibold text-primary text-sm leading-5">
					<span className="font-bold text-warning-primary">
						Ativação do hardware
					</span>
					<br />
					Caso você não tenha telemetria por hardware contratada e queira saber
					mais sobre a solução, entre em contato pelo número{" "}
					<a
						className="underline"
						href={COMMERCIAL_WHATSAPP_URL}
						rel="noopener noreferrer"
						target="_blank"
					>
						{COMMERCIAL_WHATSAPP_DISPLAY}
					</a>
					.
					<br />A telemetria por hardware é recomendada para empresas que querem
					acompanhar com precisão o consumo de combustível e outros dados.
				</p>
			</div>
		</div>
	);
};
